import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { FaExclamationTriangle } from 'react-icons/fa'
import { XMarkIcon } from '@heroicons/react/24/outline'

const toneStyles = {
  danger: {
    iconWrapper: 'bg-red-100 text-red-600 dark:bg-red-500/10 dark:text-red-300',
    confirmButton:
      'bg-red-600 text-white hover:bg-red-500 focus-visible:ring-red-500 disabled:bg-red-500/60 disabled:text-white/80',
  },
  warning: {
    iconWrapper:
      'bg-amber-100 text-amber-600 dark:bg-amber-500/10 dark:text-amber-200',
    confirmButton:
      'bg-amber-500 text-white hover:bg-amber-400 focus-visible:ring-amber-400 disabled:bg-amber-500/60 disabled:text-white/80',
  },
  neutral: {
    iconWrapper:
      'bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-slate-200',
    confirmButton:
      'bg-slate-800 text-white hover:bg-slate-700 focus-visible:ring-slate-400 disabled:bg-slate-700/60 disabled:text-white/80',
  },
}

export default function ConfirmationDialog({
  isOpen = false,
  setIsOpen = () => {},
  title = 'Confirm action',
  contextText = '',
  handleSubmit = () => {},
  confirmLabel = 'Proceed',
  cancelLabel = 'Cancel',
  isProcessing = false,
  appearance = 'danger',
  Icon = FaExclamationTriangle,
}) {
  const tone = toneStyles[appearance] ?? toneStyles.danger

  const closeModal = () => {
    if (isProcessing) return
    setIsOpen(false)
  }

  const onConfirmClick = () => {
    if (isProcessing) return
    handleSubmit()
  }

  return (
    <Transition appear show={!!isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[99]" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl border border-white/40 bg-white/95 p-6 text-left shadow-2xl transition-all backdrop-blur dark:border-white/10 dark:bg-customSurface">
                <div className="flex items-start justify-between gap-4">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${tone.iconWrapper}`}
                  >
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    aria-label="Close confirmation dialog"
                    className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:hover:bg-white/5 dark:hover:text-white"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="mt-6">
                  <Dialog.Title
                    as="h3"
                    className="text-xl font-semibold text-slate-900 dark:text-white"
                  >
                    {title}
                  </Dialog.Title>
                  {contextText ? (
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {contextText}
                    </p>
                  ) : null}
                </div>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={isProcessing}
                    className="inline-flex justify-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5"
                  >
                    {cancelLabel}
                  </button>
                  <button
                    type="button"
                    onClick={onConfirmClick}
                    disabled={isProcessing}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${tone.confirmButton}`}
                  >
                    {isProcessing ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        Processing...
                      </span>
                    ) : (
                      confirmLabel
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
